"use client";

import { useActionState, useEffect } from "react";
import Link from "next/link";
import { useSearchParams } from "next/navigation";
import { motion } from "motion/react";
import { AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { login, type AuthResult } from "@/actions/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { GlowCard } from "@/components/ui/glow-card";
import { Separator } from "@/components/ui/separator";
import { SSOButtons } from "@/components/auth/sso-buttons";
import { springPresets } from "@/lib/motion";

const initialState: AuthResult = {};

const staggerContainer = {
  animate: { transition: { staggerChildren: 0.05, delayChildren: 0.1 } },
};

const staggerItem = {
  initial: { opacity: 0, y: 10 },
  animate: { opacity: 1, y: 0 },
};

// User-friendly copy for known OAuth error codes set by /auth/callback.
// Unknown codes fall through to a generic message.
const ERROR_COPY: Record<string, string> = {
  oauth_failed: "Sign-in was cancelled or failed. Please try again.",
  oauth_exchange_failed: "Couldn't complete sign-in. Please try again.",
  oauth_cancelled: "Sign-in was cancelled.",
  no_session: "Your session expired. Please sign in again.",
};

export function LoginForm() {
  const [state, formAction, isPending] = useActionState(login, initialState);
  const searchParams = useSearchParams();
  const errorCode = searchParams.get("error");

  useEffect(() => {
    if (errorCode) {
      toast.error(ERROR_COPY[errorCode] ?? "Sign-in failed. Please try again.");
    }
  }, [errorCode]);

  return (
    <GlowCard interactive glow="hover" className="w-full max-w-md">
      <div className="p-6">
        <div className="mb-6">
          <h2 className="text-heading-2 text-foreground">Welcome back</h2>
          <p className="mt-1 text-body text-muted-foreground">
            Log in to manage your outreach.
          </p>
        </div>

        <SSOButtons mode="login" />

        <form action={formAction}>
          <motion.div
            className="space-y-4"
            initial="initial"
            animate="animate"
            variants={staggerContainer}
          >
            {state.error && (
              <motion.div
                className="glass-1 flex items-center gap-2 rounded-lg px-3 py-2.5 text-sm text-destructive"
                variants={staggerItem}
                transition={springPresets.gentle}
              >
                <AlertCircle className="size-4 shrink-0" />
                {state.error}
              </motion.div>
            )}

            <motion.div className="space-y-2" variants={staggerItem} transition={springPresets.gentle}>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                name="email"
                type="email"
                placeholder="jane@example.com"
                required
              />
            </motion.div>

            <motion.div className="space-y-2" variants={staggerItem} transition={springPresets.gentle}>
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                name="password"
                type="password"
                required
              />
            </motion.div>
          </motion.div>

          <motion.div
            className="mt-6"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ ...springPresets.gentle, delay: 0.25 }}
          >
            <Button
              type="submit"
              size="lg"
              className="w-full"
              disabled={isPending}
            >
              {isPending ? "Logging in..." : "Log in"}
            </Button>
          </motion.div>
        </form>

        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ ...springPresets.gentle, delay: 0.3 }}
        >
          <Separator className="my-5" />
          <div className="text-center">
            <p className="mb-3 text-caption text-muted-foreground">
              Don&apos;t have an account?
            </p>
            <Button variant="outline" size="sm" asChild className="w-full">
              <Link href="/signup">Create an account</Link>
            </Button>
          </div>
        </motion.div>
      </div>
    </GlowCard>
  );
}
